<script setup lang="ts"></script>

<template>
  <section id="what-i-do" class="services" aria-labelledby="services-heading">
    <header class="services-header">
      <p class="section-marker">What I work on</p>
      <h2 id="services-heading" class="services-heading display">Three things I do.</h2>
    </header>

    <ol class="service-list">
      <li class="service">
        <span class="service-num" aria-hidden="true">01</span>
        <div class="service-body">
          <h3 class="service-title">Local websites</h3>
          <p class="service-desc">
            Fast sites that load on a 4G signal out at the ranch and don't make anyone pinch-zoom on
            a phone. The goal is that a customer can find you, read what you do, and call you inside
            thirty seconds.
          </p>
        </div>
        <span class="service-tag">Web</span>
      </li>

      <li class="service">
        <span class="service-num" aria-hidden="true">02</span>
        <div class="service-body">
          <h3 class="service-title">SEO that fits a small budget</h3>
          <p class="service-desc">
            Real local SEO, priced fixed. I work the searches that actually bring in calls — your
            trade plus the town next door — not "global keyword exposure."
          </p>
        </div>
        <span class="service-tag">SEO</span>
      </li>

      <li class="service">
        <span class="service-num" aria-hidden="true">03</span>
        <div class="service-body">
          <h3 class="service-title">Site security</h3>
          <p class="service-desc">
            Certificates that expire without warning, missing response headers, DNS pointing at
            something you forgot you owned. I find those, fix them, and tell you what was there. If
            I've worked on your site and something breaks, you call my cell and I handle it.
          </p>
        </div>
        <span class="service-tag">Security</span>
      </li>
    </ol>
  </section>
</template>

<style scoped>
.services {
  margin: 4rem 0 2rem;
  padding-top: 2.75rem;
  position: relative;
}
.services::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--border) 8%,
    var(--border-bright) 50%,
    var(--border) 92%,
    transparent 100%
  );
}

.services-header {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-bottom: 2.25rem;
  max-width: 38em;
}

.services-heading {
  margin: 0;
  font-size: clamp(1.7rem, 3.4vw, 2.4rem);
  font-weight: 400;
  color: var(--text);
  letter-spacing: -0.015em;
  line-height: 1.1;
}
.services-heading em {
  font-style: italic;
  color: var(--accent-bright);
  font-variation-settings: 'opsz' 96;
}

.service-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--border);
}

.service {
  display: grid;
  grid-template-columns: 4rem 1fr auto;
  gap: 1.5rem;
  align-items: baseline;
  padding: 1.5rem 0.25rem 1.5rem 0;
  border-bottom: 1px solid var(--border);
  position: relative;
  transition:
    background 0.25s ease,
    padding-left 0.3s ease;
}
.service:hover {
  background: linear-gradient(90deg, var(--accent-tint) 0%, transparent 60%);
  padding-left: 0.5rem;
}

.service-num {
  font-family: var(--font-mono);
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  color: var(--text-faint);
  align-self: start;
  padding-top: 0.45rem;
}
.service:hover .service-num {
  color: var(--accent-bright);
}

.service-body {
  min-width: 0;
}

.service-title {
  margin: 0 0 0.4rem;
  font-family: var(--font-display);
  font-size: 1.32rem;
  font-weight: 500;
  letter-spacing: -0.005em;
  color: var(--text);
  font-variation-settings: 'opsz' 36;
}

.service-desc {
  margin: 0;
  color: var(--text-muted);
  font-size: 1rem;
  line-height: 1.6;
  max-width: 42em;
}

.service-tag {
  font-family: var(--font-mono);
  font-size: 0.65rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-faint);
  border: 1px solid var(--border-bright);
  padding: 0.3rem 0.55rem;
  border-radius: var(--radius-sm);
  align-self: start;
  margin-top: 0.4rem;
  transition:
    color 0.2s ease,
    border-color 0.2s ease;
}
.service:hover .service-tag {
  color: var(--accent);
  border-color: var(--accent);
}

@media (max-width: 640px) {
  .service {
    grid-template-columns: 2.4rem 1fr;
    gap: 1rem;
  }
  .service-tag {
    grid-column: 2;
    justify-self: start;
    margin-top: 0.5rem;
  }
}
</style>
